import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SchoolIcon from "@mui/icons-material/School";
import { useState, useEffect } from "react";
import { getAcademicYear } from "../utils/academicYear";
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import AuthService from "../Auth/AuthService";
import CheckMarksheetReceipt from "../reciept/CheckMarksheetReceipt"

export default function CheckResultPage() {

    const [search, setSearch] = useState({
        searchBy: "rollNo",
        keyword: "",
        academicYear: getAcademicYear(),
        exam: "final",
    });
    const [student, setStudent] = useState([])
    const [isFormValid, setIsFormValid] = useState(false);
    const [errors, setErrors] = useState({
        keyword: "",
        academicYear: "",
        exam: ""
    });
   
    const navigate = useNavigate();

    const validateField = (name, value) => {
        let error = "";

        switch (name) {
            case "keyword":
                if (!value || value.trim() === "") {
                    error = "Please enter a valid number";
                } else if (!/^\d+$/.test(value.trim())) {
                    error = "Please enter only numbers";
                }
                break;
            case "academicYear":
                if (!value || value.trim() === "") {
                    error = "Please select an academic year";
                }
                break;
            case "exam":
                if (!value || value.trim() === "") {
                    error = "Please select an exam type";
                }
                break;
            default:
                break;
        }

        return error;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSearch({
            ...search,
            [name]: value,
        });

        // Validate the field
        const error = validateField(name, value);
        setErrors({
            ...errors,
            [name]: error
        });
    };

    // Validate all fields and update form validity
    useEffect(() => {
        const keywordError = validateField("keyword", search.keyword);
        const academicYearError = validateField("academicYear", search.academicYear);
        const examError = validateField("exam", search.exam);

        const isValid =
            keywordError === "" &&
            academicYearError === "" &&
            examError === "" &&
            search.keyword.trim() !== "";

        setIsFormValid(isValid);

        setErrors({
            keyword: keywordError,
            academicYear: academicYearError,
            exam: examError
        });
    }, [search.keyword, search.academicYear, search.exam]);

    const [loading, setLoading] = useState(false);

    const handleSearch = async () => {
        if (!isFormValid) return;

        setLoading(true);
        try {
            const url = AuthService.pathUrl('POST', '/marksheets/check-result');
            const result = await AuthService.restPost(url, search);

            if (result) {
                const resultArray = Array.isArray(result) ? result : [result];
                setStudent(resultArray);
            } else {
                setStudent([]);
            }
        } catch (error) {
            console.error("Error fetching student data:", error);
            setStudent([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && isFormValid) {
            handleSearch();
        }
    };

    return (
        <Box
            sx={{
                maxWidth: 1000,
                mx: "auto",
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >

            <Card elevation={0}>
                <IconButton color="error" onClick={() => navigate(-1)} >
                    <ArrowBack />
                </IconButton>
                <CardContent>

                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mb={3}
                    >
                        <SchoolIcon
                            sx={{
                                fontSize: 45,
                                color: "#1b1914",
                                mr: 1,
                            }}
                        />

                        <Typography
                            variant="h5"
                            fontWeight="bold"
                        >
                            Check Result Online
                        </Typography>
                    </Box>

                    <Grid container spacing={2}>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Search By"
                                name="searchBy"
                                value={search.searchBy}
                                onChange={handleChange}
                            >
                                <MenuItem value="rollNo">
                                    Roll Number
                                </MenuItem>

                                <MenuItem value="studentId">
                                    Student ID No.
                                </MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Enter Number"
                                name="keyword"
                                value={search.keyword}
                                onChange={handleChange}
                                onKeyPress={handleKeyPress}
                                error={!!errors.keyword}
                                helperText={errors.keyword}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Academic Year"
                                name="academicYear"
                                value={search.academicYear}
                                onChange={handleChange}
                                error={!!errors.academicYear}
                                helperText={errors.academicYear}
                                required
                            >
                                <MenuItem value={search.academicYear}>
                                    {search.academicYear}
                                </MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Exam"
                                name="exam"
                                value={search.exam}
                                onChange={handleChange}
                                error={!!errors.exam}
                                helperText={errors.exam}
                                required
                            >
                                <MenuItem value="first">
                                    Unit Test
                                </MenuItem>

                                <MenuItem value="second">
                                    Half Yearly
                                </MenuItem>

                                <MenuItem value="final">
                                    Annual Exam
                                </MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<SearchIcon />}
                                sx={{
                                    height: 50,
                                    bgcolor: isFormValid ? "#1b1914" : "#999",
                                    fontWeight: "bold",
                                    '&:hover': {
                                        bgcolor: isFormValid ? "#1b1914" : "#999",
                                    }
                                }}
                                onClick={handleSearch}
                                disabled={!isFormValid}
                            >
                                Check Result
                            </Button>
                        </Grid>

                    </Grid>
                </CardContent>
                {student.length > 0 ? (
                    <CheckMarksheetReceipt receiptData={student} />
                ) : (
                    <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
                        No student found with the given criteria
                    </Typography>
                )}
            </Card>
        </Box>
    );
}